'use client';

import { Children, isValidElement, useMemo, type ReactNode } from 'react';

import { useTranslations } from 'next-intl';

import { LazyImageLightbox, useImageLightbox } from '@/components/ui/lazy-image-lightbox';
import { MarkdownImage } from '@/components/ui/markdown-image';
import { cn } from '@/lib/utils';

const MAX_VISIBLE = 4;

interface MarkdownImageGalleryProps {
  children?: ReactNode;
}

function getGalleryItemRounding({
  index,
  visibleCount,
}: {
  index: number;
  visibleCount: number;
}): string[] {
  const classes: string[] = [];
  if (index === 0) classes.push('rounded-ss-lg');
  if (index === 1) classes.push('rounded-se-lg');
  if (index === visibleCount - 2 && visibleCount > 2) classes.push('rounded-es-lg');
  if (index === visibleCount - 1) classes.push('rounded-ee-lg');
  if (visibleCount === 2 && index === 0) classes.push('rounded-es-lg');
  if (visibleCount === 2 && index === 1) classes.push('rounded-ee-lg');
  if (visibleCount === 3 && index === 2) classes.push('rounded-ee-lg', 'rounded-es-lg');
  return classes;
}

export function MarkdownImageGallery({ children }: MarkdownImageGalleryProps) {
  const t = useTranslations('imageGallery');
  const images = useMemo(() => {
    const entries: { src: string; alt: string }[] = [];
    Children.forEach(children, child => {
      if (isValidElement<{ src?: string; alt?: string }>(child) && child.props.src) {
        entries.push({ src: child.props.src, alt: child.props.alt ?? '' });
      }
    });
    return entries;
  }, [children]);

  const { lightboxProps, openLightbox, createKeyHandler } = useImageLightbox();
  const imageUrls = useMemo(() => images.map(img => img.src), [images]);

  if (images.length === 0) return null;

  if (images.length === 1) {
    return <MarkdownImage src={images[0].src} alt={images[0].alt} />;
  }

  const visibleCount = Math.min(images.length, MAX_VISIBLE);
  const extraCount = images.length - MAX_VISIBLE;

  return (
    <>
      <div
        role="group"
        aria-label={t('galleryLabel', { count: images.length })}
        className="my-4 grid max-w-lg grid-cols-2 gap-1 rounded-lg"
      >
        {images.slice(0, visibleCount).map((img, index) => (
          <div
            key={index}
            role="button"
            tabIndex={0}
            className={cn(
              'relative cursor-pointer overflow-hidden bg-muted transition-opacity outline-none hover:opacity-90 focus-visible:ring-[3px] focus-visible:ring-ring/50',
              ...getGalleryItemRounding({ index, visibleCount }),
            )}
            onClick={() => openLightbox(index)}
            onKeyDown={createKeyHandler(index)}
            aria-label={
              index === MAX_VISIBLE - 1 && extraCount > 0
                ? t('viewAll', { count: images.length })
                : img.alt || t('slideAnnouncement', { current: index + 1, total: images.length })
            }
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={img.src}
              alt={img.alt}
              className="aspect-4/3 h-full w-full object-cover"
              loading="lazy"
            />
            {index === MAX_VISIBLE - 1 && extraCount > 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <span className="text-2xl font-semibold text-white">+{extraCount}</span>
              </div>
            )}
          </div>
        ))}
      </div>
      <LazyImageLightbox images={imageUrls} {...lightboxProps} />
    </>
  );
}
